import {
    type ApiRequisition,
    type EnumRequisitionOrigin,
    EnumRequisitionStatus,
    getBadgeVariantByRequisitionStatus,
    getEnumRequisitionOriginLabel,
    getEnumRequisitionStatusLabel,
    processStateOptions,
    requisitionOriginOptions,
} from '@Model';
import {
    Badge,
    type Column,
    type ITableWithStoredSettingsProps,
    renderDateTime,
} from '@synqo/frontend-component-library';

/**
 * The sorting every Bestellungen table starts with, in the ERP as well as in the portal: the newest
 * order first.
 */
export const initialRequisitionSorting: Readonly<ITableWithStoredSettingsProps<ApiRequisition>['initialSorting']> = {
    columnName: 'created_at',
    direction: 'desc',
};

export const requisitionColumns: Readonly<Column<ApiRequisition>>[] = [
    { name: 'number', label: 'Nummer' },
    {
        name: 'site.display_string',
        label: 'Lieferanschrift',
        renderCell: ({ rowData }) => rowData.site?.address?.display_string,
    },
    {
        name: 'created_at',
        label: 'Erfasst am',
        type: 'datetime',
    },
    {
        name: 'process_state',
        label: 'Status',
        filterVariant: 'select',
        filterOptions: processStateOptions,
        renderCell: ({ cellData }) => (
            <Badge variant={getBadgeVariantByRequisitionStatus(cellData as EnumRequisitionStatus)}>
                {getEnumRequisitionStatusLabel(cellData as EnumRequisitionStatus)}
            </Badge>
        ),
    },
    {
        name: 'origin',
        label: 'Erfassungsquelle',
        filterVariant: 'select',
        filterOptions: requisitionOriginOptions,
        renderCell: ({ cellData }) => <>{getEnumRequisitionOriginLabel(cellData as EnumRequisitionOrigin)}</>,
    },
    // Requisitions carry a desired date, not a scheduled time; without one the order is "Schnellstmöglich".
    // @ai-generated 2026-07-03T23:30:00Z by dimitri.driediger@synqony.com using Claude Fable 5
    {
        name: 'date_from',
        label: 'Plandatum (von)',
        type: 'date',
        filterVariant: 'day-range',
        renderCell: ({ cellData }) => <>{formatPlanDate(cellData, 'Schnellstmöglich')}</>,
    },
    {
        name: 'date_to',
        label: 'Plandatum (bis)',
        type: 'date',
        filterVariant: 'day-range',
        renderCell: ({ cellData }) => <>{formatPlanDate(cellData, '—')}</>,
    },
];

function formatPlanDate(cellData: unknown, fallback: string): string {
    if (!cellData) {
        return fallback;
    }
    return renderDateTime(cellData as string, 'date');
}
